import { useActingRole, type ActingRole } from '@brush/auth';
import { Ticker } from '@brush/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, type ReactNode } from 'react';
import { VIEW_LABEL, type PortalView } from '../nav';
import { useSession } from '../api';
import { ownCardFor } from '../ownCard';
import { usePlayer } from '../player';
import { useCurrentUser } from '../queries';
import { roleChoicesFor, shortLabelFor } from '../roles';
import { tickerFor } from '../ticker';
import { About } from './About';

/**
 * Direction C — "The Rail", on-air side.
 *
 * The same rail across the top as room-web — the brand block is identical, which is the
 * handshake that keeps the two apps one product — over an inverted, charcoal ground. The
 * ticker carries the player's own position and never scrolls away: pinned under the header
 * on a phone, fixed to the bottom edge from tablet up.
 *
 * Signal orange means "this is you" here and is never a control, so the active tab is marked
 * in chalk. Every figure on the strip comes from the player's own record; nothing is derived
 * or defaulted, so a player with no cashes sees no cashes rather than a zero standing in for
 * an unknown.
 */
export function PortalShell({
  view,
  onNavigate,
  onOpenCards,
  children,
}: {
  view: PortalView;
  onNavigate: (view: PortalView) => void;
  /** Opens the cards screen — switch room, connect another, disconnect one. */
  onOpenCards: () => void;
  children: ReactNode;
}) {
  const { player, room, theme, canBrowse, readOnly, membership, clearPlayer } = usePlayer();
  const { signOut } = useSession();
  const me = useCurrentUser();
  const { actingRole, choose } = useActingRole();
  const queryClient = useQueryClient();

  // Offered only when there is more than one to switch between — a control with a single option
  // is furniture. The list is the server's answer about this account, so a withdrawn assignment
  // removes the option rather than leaving one that is refused when used.
  const roleChoices = roleChoicesFor(me.data?.availableRoles ?? []);

  const switchRole = (next: ActingRole) => {
    choose(next);
    // The role decides whose portal there is to show, so what is cached is not merely stale —
    // it was read by somebody else.
    queryClient.clear();
  };

  // About the signed-in player only: on a portal staff are browsing it names the player being viewed instead (#527).
  // The card is the holder's own, from their membership, and only beside their own player (#494): the player read
  // carries no card, and a member of staff browsing somebody else's portal sees none.
  const ticker = useMemo(
    () => (player ? tickerFor(player, readOnly, ownCardFor(player, membership)) : null),
    [player, readOnly, membership],
  );

  if (!player) return null;

  const tabs: PortalView[] = ['home'];
  if (room?.canLeaderboard) tabs.push('leaderboards');
  if (room?.canFantasy) tabs.push('fantasy');
  if (room?.canRatings) tabs.push('ratings');
  tabs.push('stats');

  return (
    <div className="onair min-h-screen" style={{ background: theme.bg, color: theme.text }}>
      <header className="flex h-14 items-stretch" style={{ background: '#16191d' }}>
        <span
          className="flex shrink-0 items-center bg-signal pl-[22px] pr-[26px] text-base font-bold italic tracking-[0.06em] text-white"
          style={{ clipPath: 'polygon(0 0, 100% 0, calc(100% - 14px) 100%, 0 100%)' }}
        >
          BRUSH
        </span>

        <span
          className="hidden shrink-0 items-center truncate pl-1.5 pr-4 text-[10.5px] font-semibold uppercase tracking-[0.2em] sm:flex"
          style={{ color: theme.textMuted }}
        >
          {room?.name ?? 'Brush'}
        </span>

        <nav className="hidden flex-1 items-stretch overflow-x-auto md:flex" aria-label="Portal sections">
          {tabs.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => onNavigate(tab)}
              aria-current={view === tab ? 'page' : undefined}
              className={`flex min-h-[44px] shrink-0 items-center whitespace-nowrap px-4 text-[12.5px] font-semibold uppercase tracking-[0.08em] transition ${
                view === tab
                  ? 'bg-white/5 text-white shadow-[inset_0_-4px_0_#ffffff]'
                  : 'hover:text-white'
              }`}
              style={view === tab ? undefined : { color: theme.textMuted }}
            >
              {VIEW_LABEL[tab]}
            </button>
          ))}
        </nav>

        <div className="ml-auto flex shrink-0 items-center gap-3 pr-4 md:ml-0">
          {readOnly ? (
            <span
              className="hidden px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.1em] sm:block"
              style={{ border: `1px solid ${theme.border}`, color: theme.textMuted }}
              title="You're viewing this player's portal as staff. Nothing here can be changed."
            >
              Read only
            </span>
          ) : null}

          {roleChoices.length > 1 && actingRole ? (
            <select
              value={actingRole}
              onChange={(e) => switchRole(e.target.value as ActingRole)}
              aria-label="Switch role"
              // Chalk rather than signal orange, and deliberately: orange means "this is you" in
              // the portal and is never a control. A role switcher is a control, and colouring it
              // would say it was the signed-in player.
              className="hidden bg-transparent px-2 py-1.5 text-[10px] font-bold uppercase tracking-[0.1em] outline-none md:block"
              style={{ border: `1px solid ${theme.border}`, color: theme.textMuted }}
            >
              {roleChoices.map((choice) => (
                <option key={choice.id} value={choice.id} className="text-ink-900">
                  {shortLabelFor(choice.id as ActingRole)}
                </option>
              ))}
            </select>
          ) : null}

          {membership ? (
            // Shown for one card as well as several: this is also the way to connect a card at
            // another room and to disconnect one, which a player holding exactly one still wants.
            // "My cards" rather than "Switch room" for that reason — the room is what changes most
            // often, but it is not all that is behind it.
            <button
              type="button"
              onClick={onOpenCards}
              className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.1em] transition hover:text-white"
              style={{ border: `1px solid ${theme.border}`, color: theme.textMuted }}
            >
              My cards
            </button>
          ) : null}

          {canBrowse ? (
            <button
              type="button"
              onClick={clearPlayer}
              className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.1em] transition hover:text-white"
              style={{ border: `1px solid ${theme.border}`, color: theme.textMuted }}
            >
              Change player
            </button>
          ) : null}

          <span className="hidden text-right lg:block">
            <span className="block text-[12px] font-semibold">{player.name}</span>
          </span>

          <span
            className="flex size-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
            style={{ background: theme.primary }}
          >
            {player.avatar}
          </span>

          {signOut ? (
            <button
              type="button"
              onClick={signOut}
              className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.1em] transition hover:text-white"
              style={{ border: `1px solid ${theme.border}`, color: theme.textMuted }}
            >
              Sign out
            </button>
          ) : null}
        </div>
      </header>

      {/* Phone — the ticker sits under the rail and holds still. */}
      <div className="md:hidden">
        {ticker ? <Ticker facts={ticker.facts} label={ticker.label} labelIsSelf={ticker.labelIsSelf} /> : null}
      </div>

      <div className="mx-auto max-w-6xl p-6 pb-24 md:pb-16">
        {children}

        {/* Small print, at the foot of the content rather than in the rail — see About. */}
        <div className="mt-10 border-t pt-4" style={{ borderColor: theme.border }}>
          <About />
        </div>
      </div>

      {/* Tablet and desktop — the ticker is fixed to the bottom edge and crawls. */}
      <div className="fixed inset-x-0 bottom-0 z-30 hidden md:block">
        {ticker ? <Ticker facts={ticker.facts} label={ticker.label} labelIsSelf={ticker.labelIsSelf} /> : null}
      </div>

      {/* Phone — five views collapse to bottom tabs. */}
      <nav
        className="fixed inset-x-0 bottom-0 z-30 grid md:hidden"
        style={{
          background: '#16191d',
          borderTop: `1px solid ${theme.border}`,
          gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))`,
        }}
        aria-label="Portal sections"
      >
        {tabs.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => onNavigate(tab)}
            aria-current={view === tab ? 'page' : undefined}
            className={`grid min-h-[52px] place-items-center px-1 text-[9.5px] font-bold uppercase tracking-[0.1em] ${
              view === tab ? 'text-white shadow-[inset_0_3px_0_#ffffff]' : ''
            }`}
            style={view === tab ? undefined : { color: theme.textMuted }}
          >
            {VIEW_LABEL[tab]}
          </button>
        ))}
      </nav>
    </div>
  );
}
